import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FavoriteListDto } from '@/services/platform/api/model';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import {
  useAddFavoriteItem,
  useFindFavoriteList,
  useRemoveFavoriteItem,
} from '@/services/platform/data/shop';
import { getMeFavoriteItemsV1 } from '@/services/platform/api/me-—-favorites/me-—-favorites';
import { CreateNewListSheet } from '@/components/shared/profile/favorites/sheets/CreateNewListSheet';
import { FavoritesActionToast } from '@/components/shared/profile/favorites/ui/FavoritesActionToast';
import { AddToWishlistSheet } from '../sheets/AddToWishlistSheet';
import { HeartToggle } from './HeartToggle';

export interface ProductFavoriteToggleHandle {
  /** Opens the "add to wishlist" sheet — for triggers other than the heart icon itself (e.g. ItemAddedSheet). */
  open: () => void;
}

export interface ProductFavoriteToggleProps {
  product: {
    publicGuid: string | undefined;
    title?: string | null;
    vendor?: string | null;
    imageUrl?: string | null;
  };
  /** The user's 'Shop' favorite lists — fetch once at the parent screen and share across cards. */
  favoriteLists: FavoriteListDto[];
  refetchFavoriteLists: () => Promise<void>;
  size?: number;
  className?: string;
  /** Notified whenever the favorited state changes, for parents that also reflect it elsewhere (e.g. ItemAddedSheet). */
  onFavoritedChange?: (isFavorited: boolean) => void;
  /**
   * Batch mode — for grids rendering many products, where the parent already knows which
   * list (if any) contains each product (via `useShopFavoriteIndex`) and refetches that
   * index as a whole. When `onRefetchNeeded` is provided, this component skips its own
   * per-product `/favorites/lookup` call and trusts `favoriteListGuid` instead. Omit both
   * for a single-product screen (e.g. product detail) to keep the simpler, self-contained lookup.
   */
  favoriteListGuid?: string;
  onRefetchNeeded?: () => Promise<void>;
}

/**
 * Self-contained "favorite" control for a shop product: the heart icon plus the
 * add-to-wishlist / create-list sheets and confirmation toast. Used on both the
 * product detail screen and product listing cards so the add/remove-favorite
 * behaviour stays identical in both places.
 */
export const ProductFavoriteToggle = forwardRef<ProductFavoriteToggleHandle, ProductFavoriteToggleProps>(
  function ProductFavoriteToggle(
    {
      product,
      favoriteLists,
      refetchFavoriteLists,
      size = 24,
      className,
      onFavoritedChange,
      favoriteListGuid,
      onRefetchNeeded,
    },
    ref
  ) {
    const t = useClientTranslation();
    const navigate = useNavigate();
    const locale = useLocaleStore(state => state.locale);
    // Batch mode (grids via useShopFavoriteIndex): the parent already knows the answer, so
    // skip this component's own per-product lookup call entirely by passing no item id.
    const isBatchMode = onRefetchNeeded != null;
    const { data: ownFavoriteList, refetch: refetchOwnFavoriteList } = useFindFavoriteList(
      'Product',
      isBatchMode ? undefined : product.publicGuid
    );
    const { addItem: addFavoriteItem, createList } = useAddFavoriteItem();
    const { removeItem: removeFavoriteItem } = useRemoveFavoriteItem();

    const existingFavoriteListGuid = isBatchMode ? favoriteListGuid : ownFavoriteList?.publicGuid;
    const refetchExistingFavoriteList = isBatchMode ? onRefetchNeeded! : refetchOwnFavoriteList;
    const isFavorited = existingFavoriteListGuid != null;
    const [isAddToWishlistOpen, setAddToWishlistOpen] = useState(false);
    const [isCreateListOpen, setCreateListOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastListGuid, setToastListGuid] = useState<string | null>(null);
    const [toastKey, setToastKey] = useState(0);

    useEffect(() => {
      onFavoritedChange?.(isFavorited);
    }, [isFavorited, onFavoritedChange]);

    useImperativeHandle(ref, () => ({ open: () => setAddToWishlistOpen(true) }), []);

    async function addProductToList(list: FavoriteListDto) {
      if (!product.publicGuid || !list.publicGuid) return;
      await addFavoriteItem(list.publicGuid, {
        itemType: 'Product',
        itemExternalId: product.publicGuid,
        itemName: product.title ?? product.vendor ?? null,
        itemImageUrl: product.imageUrl ?? null,
      });
      setAddToWishlistOpen(false);
      setToastMessage(t('App.Shop.Wishlist.AddedToList', { listName: list.name ?? '' }));
      setToastListGuid(list.publicGuid);
      setToastKey(k => k + 1);
      await refetchExistingFavoriteList();
    }

    async function handleSelectFavoriteList(listGuid: string) {
      const list = favoriteLists.find(l => l.publicGuid === listGuid);
      if (!list) return;
      await addProductToList(list);
    }

    async function handleDeselectFavoriteList(listGuid: string) {
      if (!product.publicGuid) return;
      const list = favoriteLists.find(l => l.publicGuid === listGuid);
      const response = await getMeFavoriteItemsV1(listGuid);
      if (response.status !== 200) return;
      const item = response.data.find(
        i => i.itemType === 'Product' && i.itemExternalId === product.publicGuid
      );
      if (!item?.publicGuid) return;
      const removed = await removeFavoriteItem(listGuid, item.publicGuid);
      if (!removed) return;
      setAddToWishlistOpen(false);
      setToastMessage(t('App.Shop.Wishlist.RemovedFromList', { listName: list?.name ?? '' }));
      setToastListGuid(null);
      setToastKey(k => k + 1);
      await refetchExistingFavoriteList();
    }

    async function handleCreateList(name: string) {
      const created = await createList(name, 'Shop');
      if (!created) return;
      await refetchFavoriteLists();
      setCreateListOpen(false);
      await addProductToList(created);
    }

    return (
      <>
        <HeartToggle
          isFavorited={isFavorited}
          onToggle={() => setAddToWishlistOpen(true)}
          size={size}
          className={className}
        />

        {/*
         * Sheet/toast content renders via a Radix Portal (teleported out of the DOM tree),
         * but React still bubbles its synthetic click events through the *React* tree — which
         * nests this component inside a clickable product card in grid contexts. Without this
         * stopPropagation, selecting a list row bubbles up to the card's onClick and navigates
         * to the product detail page before the toast can show.
         */}
        <div onClick={e => e.stopPropagation()}>
          <AddToWishlistSheet
            open={isAddToWishlistOpen}
            onOpenChange={setAddToWishlistOpen}
            product={{
              image: product.imageUrl ?? undefined,
              brand: product.vendor ?? undefined,
              name: product.title ?? undefined,
            }}
            lists={favoriteLists}
            selectedListGuid={existingFavoriteListGuid}
            onSelectList={handleSelectFavoriteList}
            onDeselectList={handleDeselectFavoriteList}
            onCreateNewList={() => setCreateListOpen(true)}
          />

          <CreateNewListSheet
            open={isCreateListOpen}
            onOpenChange={setCreateListOpen}
            listType="Shop"
            onCreate={handleCreateList}
          />

          <FavoritesActionToast
            key={toastKey}
            open={toastMessage !== null}
            anchor="bottom"
            message={toastMessage ?? ''}
            actionLabel={toastListGuid ? t('App.Shop.Wishlist.GoToList') : undefined}
            onAction={
              toastListGuid
                ? () => {
                    const localeSegment = locale?.split('-')[0] ?? 'en';
                    navigate(`/${localeSegment}/profile/favorites?list=${toastListGuid}`);
                  }
                : undefined
            }
            onDismiss={() => setToastMessage(null)}
          />
        </div>
      </>
    );
  }
);
